/**
 * The Shycut mark: a head peeking up from behind the frame's edge. "Shy" is the
 * person who'd rather not be seen; the frame cutting them off is the "cut".
 * One geometry (on a 32-unit grid) feeds the app logo, the favicon and every
 * app icon (`npm run assets:icons`), so they can never drift apart.
 */
export const MARK = {
  size: 32,
  /** Corner radius of the frame. */
  radius: 9,
  /** The head: a circle mostly below the frame's bottom edge, a little off-centre. */
  head: { cx: 13.5, cy: 31.5, r: 11.5 },
  /** Two upright oval eyes. */
  eyes: [
    { cx: 10.4, cy: 24.9 },
    { cx: 17.2, cy: 24.9 },
  ],
  eye: { rx: 1.55, ry: 2.05 },
  colors: { ground: '#c6f25e', ink: '#0b0b0c' },
} as const;

/**
 * Standalone SVG of the mark. `radius: 0` gives a full-bleed square for
 * platforms that apply their own mask; `inset` then keeps the motif inside a
 * safe zone (the head sits on an invisible baseline instead of the edge).
 */
export function markSvg({
  radius = MARK.radius,
  inset = 0,
  id = 'm',
  eyeClass,
}: { readonly radius?: number; readonly inset?: number; readonly id?: string; readonly eyeClass?: string } = {}): string {
  const { size, head, eyes, eye, colors } = MARK;
  const scale = (size - inset * 2) / size;
  // `eyeClass` lets a page's stylesheet blink the eyes, as the app's logo does on hover.
  const eyeAttribute = eyeClass ? ` class="${eyeClass}"` : '';
  const eyeShapes = eyes.map((e) => `<ellipse${eyeAttribute} cx="${e.cx}" cy="${e.cy}" rx="${eye.rx}" ry="${eye.ry}" fill="${colors.ground}"/>`).join('');
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}">`,
    `<defs><clipPath id="${id}"><rect width="${size}" height="${size}" rx="${radius}"/></clipPath></defs>`,
    `<rect width="${size}" height="${size}" rx="${radius}" fill="${colors.ground}"/>`,
    `<g transform="translate(${inset} ${inset}) scale(${scale})"><g clip-path="url(#${id})">`,
    `<circle cx="${head.cx}" cy="${head.cy}" r="${head.r}" fill="${colors.ink}"/>${eyeShapes}`,
    `</g></g></svg>`,
  ].join('');
}
